import * as React from 'react';
import { useEffect, useState } from 'react';
import { Calendar, Clear } from '@youlify/icons';
import classNames from 'classnames';
import type { Dayjs } from 'dayjs';

import ConfigProvider from '../../config-provider';
import DatePicker from '../../date-picker';
import Dropdown from '../../dropdown';
import { useStyle } from '../style/input-trigger';
import { FilterProps, FilterValue } from '../types';
import FilterSelectPanel from './FilterSelectPanel';
import FilterTrigger from './FilterTrigger';

const Filter: React.FC<FilterProps> = ({
  type = 'input',
  dropdownType,
  value,
  initialValue,
  noneValue,
  showSelectAll,
  allowUncheck,
  options = [],
  needBackFill,
  customBackFill,
  dropdownProps,
  datePickerProps,
  style,
  onSearch,
  onInputChange,
  onValueChange,
  onChange,
  ...restProps
}) => {
  const [wrapSSR, hashId] = useStyle('youlify-filter');
  const [selectedValue, setSelectedValue] = useState<FilterValue>();
  const [searchValue, setSearchValue] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const isInputType = type === 'input';
  const isSearchType = type === 'search';
  const isSelectType = type === 'select';
  const isDatePickerType = type === 'date-picker';
  const isDatePickerRangeType = type === 'date-picker-range';

  if ((isSearchType || isSelectType || isDatePickerType || isDatePickerRangeType) && !dropdownType)
    dropdownType = 'single';

  if (noneValue === undefined) {
    if (isInputType || isSearchType || dropdownType === 'single') noneValue = '';
    else if (isSelectType && dropdownType === 'multiple') noneValue = [];
    else noneValue = undefined;
  }

  const isWithDropdown = !!dropdownType;
  const isCustomDropdown = dropdownType === 'custom';
  const filterSelectPanelMode = dropdownType === 'single' ? 'single' : 'multiple';

  const callPropsValueChange = (value?: FilterValue) => {
    onValueChange?.(value);
    onChange?.(value);
  };

  const handleFilterSelectPanelChange = (value?: FilterValue) => {
    setSelectedValue(value);
    if (isSearchType && !value) setSearchValue('');
    callPropsValueChange(value);
  };

  const handleFilterTriggerChange = (value?: FilterValue) => {
    setSelectedValue(value);
    callPropsValueChange(value);
  };

  const handleFilterTriggerInputChange = (inputValue: string) => {
    if (isSearchType) setSearchValue(inputValue);
    onInputChange?.(inputValue);
    onSearch?.(inputValue);
  };

  const handleSelectPanelClose = () => setDropdownVisible(false);

  useEffect(() => {
    if (value !== selectedValue) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setSelectedValue(value !== undefined ? value : initialValue);
  }, []);

  const renderInnerDropdown = () =>
    isDatePickerType ? (
      <DatePicker
        open={true}
        value={selectedValue as Dayjs | null | undefined}
        onChange={(date) => {
          handleFilterSelectPanelChange(date);
          handleSelectPanelClose();
        }}
        {...datePickerProps}
        style={{
          position: 'absolute',
          bottom: 0,
          insetInlineStart: 0,
          opacity: 0,
          pointerEvents: 'none',
          ...datePickerProps?.style,
        }}
      />
    ) : (
      <FilterSelectPanel
        mode={filterSelectPanelMode}
        value={selectedValue}
        noneValue={noneValue}
        searchValue={searchValue}
        showSelectAll={showSelectAll}
        allowUncheck={allowUncheck}
        options={options}
        onChange={handleFilterSelectPanelChange}
        onClose={handleSelectPanelClose}
      />
    );

  const renderFilterTriggerWithDropdownInner = () => (
    <Dropdown
      trigger={['click']}
      destroyPopupOnHide={true}
      open={dropdownVisible}
      onOpenChange={setDropdownVisible}
      {...dropdownProps}
      dropdownRender={
        isCustomDropdown
          ? (originNode) =>
              dropdownProps?.dropdownRender?.({
                originNode,
                value: selectedValue,
                onChange: handleFilterSelectPanelChange,
                onClose: handleSelectPanelClose,
              })
          : renderInnerDropdown
      }
    >
      <div style={style}>
        <FilterTrigger
          {...restProps}
          type={type}
          dropdownType={dropdownType}
          dropdownVisible={dropdownVisible}
          value={selectedValue}
          noneValue={noneValue}
          options={options}
          needBackFill={needBackFill}
          customBackFill={customBackFill}
          datePickerProps={datePickerProps}
          onInputChange={handleFilterTriggerInputChange}
          onChange={handleFilterTriggerChange}
          onClick={() => setDropdownVisible(true)}
        />
      </div>
    </Dropdown>
  );

  const renderFilterTriggerWithDropdown = () =>
    isDatePickerRangeType ? (
      <ConfigProvider
        theme={{
          components: {
            DatePicker: {
              controlHeight: 40,
              colorText: '#141414',
              colorBgContainer: selectedValue
                ? restProps?.baseSelectedBgColor || '#e2ebff'
                : undefined,
              colorBorder: '#141414',
              colorTextPlaceholder: '#141414',
            },
          },
        }}
      >
        <DatePicker.RangePicker
          className={classNames('youlify-filter-date-picker-range-trigger', hashId)}
          separator="-"
          prefix={<Calendar />}
          suffixIcon={null}
          allowClear={{ clearIcon: <Clear /> }}
          open={dropdownVisible}
          // @ts-ignore
          value={selectedValue}
          onOpenChange={setDropdownVisible}
          // @ts-ignore
          onChange={(date) => handleFilterSelectPanelChange(date)}
          {...datePickerProps}
          style={style}
        />
      </ConfigProvider>
    ) : (
      renderFilterTriggerWithDropdownInner()
    );

  const renderFilterTriggerWithoutDropdown = () => (
    <FilterTrigger
      {...restProps}
      type={type}
      dropdownType={dropdownType}
      value={value !== undefined ? value : initialValue}
      noneValue={noneValue}
      style={style}
      onInputChange={handleFilterTriggerInputChange}
      onChange={callPropsValueChange}
    />
  );

  return wrapSSR(
    isWithDropdown ? renderFilterTriggerWithDropdown() : renderFilterTriggerWithoutDropdown(),
  );
};

export default Filter;
